import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Alert, Chip, IconButton, Tooltip,
  TablePagination, TextField, MenuItem,
} from '@mui/material';
import { Home, Print } from '@mui/icons-material';
import axios from 'axios';
import CompanyHeader from './CompanyHeader';
import { fmt as fmtAmt, sym, locale } from '../utils/currency';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtDate = (d) => new Date(d).toLocaleDateString(locale(), { year: 'numeric', month: 'long', day: 'numeric' });

const STATUS_COLOR = { Open: 'warning', Partial: 'info', Paid: 'success', reversed: 'error' };

const InvoicesList = () => {
  const navigate = useNavigate();
  const [invoices,  setInvoices]  = useState([]);
  const [error,     setError]     = useState('');
  const [printDoc,  setPrintDoc]  = useState(null);
  const [page,      setPage]      = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    axios.get(`${API}/api/finance/invoices`, { headers: hdrs() })
      .then(r => setInvoices(r.data?.data || []))
      .catch(err => setError(err.response?.data?.error || 'Failed to load invoices'));
  }, []);

  useEffect(() => {
    const clear = () => setPrintDoc(null);
    window.addEventListener('afterprint', clear);
    return () => window.removeEventListener('afterprint', clear);
  }, []);

  const handlePrint = (inv) => {
    setPrintDoc(inv);
    setTimeout(() => window.print(), 60);
  };

  return (
    <>
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #inv-print-area, #inv-print-area * { visibility: visible; }
          #inv-print-area { position: absolute; left: 0; top: 0; width: 100%; padding: 40px; box-sizing: border-box; font-family: serif; }
        }
        @media screen { #inv-print-area { display: none; } }
      `}</style>

      {printDoc && (
        <Box id="inv-print-area">
          <CompanyHeader title="Invoice" subtitle={printDoc.invoiceNo} />
          <Box textAlign="center" mb={2}>
            <Typography variant="body2">Date: {fmtDate(printDoc.date)}</Typography>
          </Box>
          <Box sx={{ border: '1px solid #ccc', p: 2, mb: 2 }}>
            <Typography variant="body2"><strong>Billed To:</strong> {printDoc.learnerAdmNo}</Typography>
            <Typography variant="body2"><strong>Stage:</strong> {printDoc.stage || '—'}</Typography>
          </Box>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginBottom: 16 }}>
            <thead>
              <tr style={{ backgroundColor: '#f0f0f0' }}>
                <th style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'left' }}>Description</th>
                <th style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'left' }}>Reason / Ref</th>
                <th style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'right' }}>Amount ({sym()})</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ border: '1px solid #ccc', padding: '6px 10px' }}>{printDoc.description}</td>
                <td style={{ border: '1px solid #ccc', padding: '6px 10px' }}>{printDoc.reason || '—'}</td>
                <td style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'right' }}>{fmtAmt(printDoc.amount)}</td>
              </tr>
              <tr style={{ fontWeight: 'bold', backgroundColor: '#f9f9f9' }}>
                <td colSpan={2} style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'right' }}>TOTAL</td>
                <td style={{ border: '1px solid #ccc', padding: '6px 10px', textAlign: 'right' }}>{sym()} {fmtAmt(printDoc.amount)}</td>
              </tr>
            </tbody>
          </table>
          <Typography variant="caption">GL Account: {printDoc.glAccount || '—'}</Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Status: <strong>{printDoc.status}</strong>{' '}
            {printDoc.paid > 0 && `· Paid: ${sym()} ${fmtAmt(printDoc.paid)}`}
          </Typography>
          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="caption">Prepared by: _______________________</Typography>
            <Typography variant="caption">Authorized by: _______________________</Typography>
          </Box>
        </Box>
      )}

      <Box sx={{ maxWidth: 950, mx: 'auto', mt: 4, mb: 4 }}>
        <Card elevation={3}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h5" color="primary">Invoices List</Typography>
              <Tooltip title="Back to Dashboard">
                <IconButton onClick={() => navigate('/dashboard')} sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                  <Home />
                </IconButton>
              </Tooltip>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Box display="flex" gap={2} mb={1.5} flexWrap="wrap" alignItems="center">
              <TextField select size="small" label="Status" value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setPage(0); }} sx={{ minWidth: 140 }}>
                <MenuItem value="">All</MenuItem>
                {['Open','Partial','Paid','reversed'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                {(() => { const f = statusFilter ? invoices.filter(i => i.status === statusFilter) : invoices; return `${f.length} invoice${f.length !== 1 ? 's' : ''}`; })()}
              </Typography>
            </Box>

            {invoices.length === 0 ? (
              <Typography color="text.secondary">No invoices found.</Typography>
            ) : (() => {
              const filtered = statusFilter ? invoices.filter(i => i.status === statusFilter) : invoices;
              const paged    = filtered.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
              return (
              <>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell><strong>Invoice No</strong></TableCell>
                    <TableCell><strong>Learner</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell align="right"><strong>Amount ({sym()})</strong></TableCell>
                    <TableCell align="right"><strong>Paid ({sym()})</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Stage</strong></TableCell>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell align="center"><strong>Print</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map(inv => (
                    <TableRow key={inv.invoiceNo} hover>
                      <TableCell><strong>{inv.invoiceNo}</strong></TableCell>
                      <TableCell>{inv.learnerAdmNo}</TableCell>
                      <TableCell>{inv.description}</TableCell>
                      <TableCell align="right">{fmtAmt(inv.amount)}</TableCell>
                      <TableCell align="right" sx={{ color: inv.paid > 0 ? 'success.main' : 'text.secondary' }}>
                        {inv.paid > 0 ? fmtAmt(inv.paid) : '—'}
                      </TableCell>
                      <TableCell>
                        <Chip label={inv.status} size="small"
                          color={STATUS_COLOR[inv.status] || 'default'} variant="outlined" />
                      </TableCell>
                      <TableCell>{inv.stage}</TableCell>
                      <TableCell>{new Date(inv.date).toLocaleDateString()}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Print Invoice">
                          <IconButton size="small" onClick={() => handlePrint(inv)}>
                            <Print fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={filtered.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                rowsPerPageOptions={[25, 50, 100]}
              />
              </>
              );
            })()}
          </CardContent>
        </Card>
      </Box>
    </>
  );
};

export default InvoicesList;
